import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRisks } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';

const riskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  likelihood: z.number().min(1).max(5),
  impact: z.number().min(1).max(5),
  status: z.enum(['identified', 'assessing', 'treating', 'monitoring', 'closed']),
});

type RiskFormData = z.infer<typeof riskSchema>;

interface RiskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeId: string;
  relatedType: string;
}

export function RiskDialog({ open, onOpenChange, nodeId, relatedType }: RiskDialogProps) {
  const { organization } = useAuth();
  const { createRisk } = useRisks(organization?.id);
  
  const calculateRiskScore = (likelihood: number, impact: number) => {
    const score = likelihood * impact;
    let severity: 'Very Low' | 'Low' | 'Medium' | 'High' | 'Very High' = 'Very Low';
    if (score >= 20) severity = 'Very High';
    else if (score >= 15) severity = 'High';
    else if (score >= 10) severity = 'Medium';
    else if (score >= 5) severity = 'Low';
    return { score, severity };
  };
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<RiskFormData>({
    resolver: zodResolver(riskSchema),
    defaultValues: {
      likelihood: 3,
      impact: 3,
      status: 'identified',
    },
  });

  const watchedLikelihood = watch('likelihood');
  const watchedImpact = watch('impact');
  const { score, severity } = calculateRiskScore(watchedLikelihood, watchedImpact);

  const onSubmit = async (data: RiskFormData) => {
    try {
      await createRisk({
        title: data.title,
        cause: data.description,
        consequence: data.description,
        likelihood: data.likelihood,
        impact: data.impact,
        status: data.status,
        related_to_type: relatedType,
        related_to_id: nodeId,
        org_id: organization!.id,
      });
      
      reset();
      onOpenChange(false);
    } catch (error) {
      // Error already handled by store
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Very Low':
        return 'text-muted-foreground';
      case 'Low':
        return 'text-green-600 dark:text-green-400';
      case 'Medium':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'High':
        return 'text-orange-600 dark:text-orange-400';
      case 'Very High':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Risk Assessment</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="title">Risk Title *</Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Enter risk title"
            />
            {errors.title && (
              <p className="text-sm text-danger">{errors.title.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Describe the risk in detail"
              className="min-h-[100px]"
            />
            {errors.description && (
              <p className="text-sm text-danger">{errors.description.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="likelihood">Likelihood (1-5) *</Label>
              <Select
                value={watchedLikelihood?.toString()}
                onValueChange={(value) => setValue('likelihood', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Very Low</SelectItem>
                  <SelectItem value="2">2 - Low</SelectItem>
                  <SelectItem value="3">3 - Medium</SelectItem>
                  <SelectItem value="4">4 - High</SelectItem>
                  <SelectItem value="5">5 - Very High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="impact">Impact (1-5) *</Label>
              <Select
                value={watchedImpact?.toString()}
                onValueChange={(value) => setValue('impact', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Very Low</SelectItem>
                  <SelectItem value="2">2 - Low</SelectItem>
                  <SelectItem value="3">3 - Medium</SelectItem>
                  <SelectItem value="4">4 - High</SelectItem>
                  <SelectItem value="5">5 - Very High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium">Risk Score:</span>
              <span className="text-lg font-bold">{score}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium">Severity:</span>
              <span className={`font-bold ${getSeverityColor(severity)}`}>
                {severity}
              </span>
            </div>
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <Select
              value={watch('status')}
              onValueChange={(value) => setValue('status', value as 'identified' | 'assessing' | 'treating' | 'monitoring' | 'closed')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="identified">Identified</SelectItem>
                <SelectItem value="assessing">Assessing</SelectItem>
                <SelectItem value="treating">Treating</SelectItem>
                <SelectItem value="monitoring">Monitoring</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              Create Risk
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}